/**
 * Affiliate Attribution SDK
 *
 * Runs on every storefront page. Responsibilities:
 *   1. On landing — read tracking params from URL, write a first-party cookie.
 *   2. On add-to-cart — resolve the discount code from the server and apply it
 *      to the Shopify session. The code is never stored in the browser.
 *
 * Config is injected by affiliate_tracker.liquid via window.__aff before this
 * script executes.
 */
(function () {
  "use strict";
  
  var cfg = window.__aff || {};
  var PLATFORM_KEY = cfg.platformKey || "";
  var CONVEX_URL = (cfg.convexSiteUrl || "").replace(/\/$/, "");
  var COOKIE_NAME = "_aff_attr";
  var CART_ADD_RE = /\/cart\/add(\.js)?(\?|$)/;

  // ── Attribution cookie ──────────────────────────────────────────────────────

  function writeAttribution(linkId, token, contactId, windowDays) {
    var expires = Date.now() + windowDays * 864e5;
    var value = encodeURIComponent(
      JSON.stringify({
        link_id: linkId,
        token: token,
        contact_id: contactId,
        expires: expires,
      })
    );
    document.cookie =
      COOKIE_NAME +
      "=" +
      value +
      "; path=/; max-age=" +
      windowDays * 86400 +
      "; SameSite=Lax";
  }

  function readAttribution() {
    var match = document.cookie.match(
      new RegExp("(?:^|; )" + COOKIE_NAME + "=([^;]*)")
    );
    if (!match) return null;
    try {
      var data = JSON.parse(decodeURIComponent(match[1]));
      if (data.expires && Date.now() > data.expires) return null;
      return data;
    } catch (_) {
      return null;
    }
  }

  // ── URL params → cookie ─────────────────────────────────────────────────────

  function captureParams() {
    if (!PLATFORM_KEY) return;
    // Prefer cfg.rawSearch — captured by the inline script before any theme JS
    // could call history.replaceState and wipe the query string (e.g. Dawn theme).
    var search = cfg.rawSearch || window.location.search;
    var params = new URLSearchParams(search);
    if (params.get("platform") !== PLATFORM_KEY) return;

    var linkId = parseInt(params.get("link_id"), 10);
    var token = params.get("token") || "";
    var contactId = params.get("contact_id") || "";
    var windowDays = parseInt(params.get("attribution_window"), 10) || 90;

    if (!linkId || !token || !contactId) return;
    writeAttribution(linkId, token, contactId, windowDays);
  }

  // ── Discount resolution ─────────────────────────────────────────────────────

  var discountApplied = false;

  function applyDiscount() {
    if (discountApplied) return;
    var attr = readAttribution();
    if (!attr || !CONVEX_URL) return;

    fetch(CONVEX_URL + "/resolve-discount", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ link_id: attr.link_id, token: attr.token }),
    })
      .then(function (res) {
        if (!res.ok) return null;
        return res.json();
      })
      .then(function (body) {
        if (!body || !body.discount_code) return;
        discountApplied = true;
        // Apply the code to the Shopify session via the discount endpoint.
        // The fetch sets the discount cookie server-side; the response is
        // discarded — the code is not stored anywhere in the browser.
        return fetch("/discount/" + encodeURIComponent(body.discount_code));
      })
      .catch(function () {
        // Non-fatal — the Shopify webhook will catch missed attributions.
      });
  }

  // ── Cart intercept ──────────────────────────────────────────────────────────
  // Intercept fetch and XHR calls to /cart/add so we apply the discount the
  // moment the customer first adds an item, before they reach checkout.

  function hookCartAdd(callback) {
    // fetch intercept
    var origFetch = window.fetch;
    window.fetch = function (input, _init) {
      var url =
        typeof input === "string"
          ? input
          : input && typeof input === "object"
          ? input.url || ""
          : "";
      var promise = origFetch.apply(this, arguments);
      if (CART_ADD_RE.test(url)) {
        promise.then(callback).catch(function () {});
      }
      return promise;
    };

    // XMLHttpRequest intercept
    var origOpen = XMLHttpRequest.prototype.open;
    XMLHttpRequest.prototype.open = function (_method, url) {
      if (CART_ADD_RE.test(String(url))) {
        this.addEventListener("load", callback);
      }
      return origOpen.apply(this, arguments);
    };
  }

  // ── Init ────────────────────────────────────────────────────────────────────

  captureParams();
  hookCartAdd(applyDiscount);

  // If the visitor already has a cart (e.g. returning visit), apply the
  // discount immediately rather than waiting for the next add-to-cart.
  if (readAttribution() && CONVEX_URL) {
    fetch("/cart.js")
      .then(function (r) {
        return r.json();
      })
      .then(function (cart) {
        if (cart && cart.item_count > 0) applyDiscount();
      })
      .catch(function () {});
  }
})();
